import Phaser from "phaser";
import {
  markBootLoadingFailed,
  markBootLoadingReady,
  pushBootLoadingLog,
  setBootLoadingProgress,
} from "../bootstrap/bootLoading";
import { createBootConfiguration } from "../core/Boot";
import { getRuntimeSession } from "../core/RuntimeSession";
import type { HomeSceneInitData } from "./HomeScene";
import {
  SYMBOL_SEQUENCE_FONT_CONFIGS,
  SYMBOL_SEQUENCE_FONT_CELL_HEIGHT,
  SYMBOL_SEQUENCE_FONT_CELL_WIDTH,
  SYMBOL_SEQUENCE_FONT_CHARS,
  SYMBOL_SEQUENCE_FONT_CHARS_PER_ROW,
  SYMBOL_SEQUENCE_FONT_IMAGE_KEY,
  SYMBOL_SEQUENCE_FONT_IMAGE_PATH,
  SYMBOL_SEQUENCE_FONT_KEY,
  NOTE_OPTIONS_FONT_IMAGE_KEY,
  NOTE_OPTIONS_FONT_IMAGE_PATH,
  NOTE_OPTIONS_FONT_KEY,
  NOTE_OPTIONS_FONT_CELL_WIDTH,
  NOTE_OPTIONS_FONT_CELL_HEIGHT,
  NOTE_OPTIONS_FONT_CHARS_PER_ROW,
  SIMSUN_ASCII_FONT_CONFIG_LIST,
  SYMBOL_SPRITE_FONT_IMAGE_KEY,
  SYMBOL_SPRITE_FONT_IMAGE_PATH,
  SYMBOL_SPRITE_FONT_KEY,
  SYMBOL_SPRITE_FONT_CELL_WIDTH,
  SYMBOL_SPRITE_FONT_CELL_HEIGHT,
  SYMBOL_SPRITE_FONT_CHARS,
  SYMBOL_SPRITE_FONT_CHARS_PER_ROW,
  type BitmapFontRegistrationConfig,
} from "../view/SymbolSequenceFont";
import { DebugOverlay } from "../view/DebugOverlay";

export const LEVEL0_UNPATTERNED_IMAGE_KEY = "level0-unpatterned-image";
export const LEVEL0_UNPATTERNED_IMAGE_PATH = "/unpatterned-level0.png";
export const HOME_BGM_KEY = "home-bgm";
export const HOME_BGM_PATH = "/audio/bgm/The_Hidden_Pattern.mp3";
export const LEVEL_BGM_KEY = "level-bgm";
export const LEVEL_BGM_PATH = "/audio/bgm/Rain_on_the_Lattice.mp3";
export const LEVEL0_BGM_KEY = "level0-bgm";
export const LEVEL0_BGM_PATH = "/audio/bgm/Locked_Behind_Glass.mp3";
export const LEVEL_SELECT_BGM_KEY = "level-select-bgm";
export const LEVEL_SELECT_BGM_PATH = "/audio/bgm/Waiting_for_Input.mp3";
export const DEFAULT_BGM_VOLUME = 0.4; // 有傻逼说我们的游戏 BGM 太大了，给调小一点

export class BootScene extends Phaser.Scene {
  public static readonly KEY = "BootScene";

  private loaderFailureMessage: string | null = null;

  public constructor() {
    super(BootScene.KEY);
  }

  public preload(): void {
    this.loaderFailureMessage = null;
    pushBootLoadingLog("ASSET", "queueing boot resources");
    setBootLoadingProgress(48, "queueing boot resources");
    this.load.on("filecomplete", (key: string, type: string) => {
      const progress = Math.round(this.load.progress * 100);
      const stagedProgress = 48 + Math.round(this.load.progress * 40);
      setBootLoadingProgress(stagedProgress, `${type}:${key}`);
      pushBootLoadingLog("FILE", `${progress}% ${type}:${key}`);
    });
    this.load.on("loaderror", (file: Phaser.Loader.File) => {
      const failure = `${file.type}:${file.key}`;
      this.loaderFailureMessage ??= failure;
      markBootLoadingFailed("Failed to load required assets", failure);
    });
    this.load.once("complete", () => {
      if (this.loaderFailureMessage !== null) {
        return;
      }
      setBootLoadingProgress(90, "asset preload complete");
      pushBootLoadingLog("OK", "asset preload complete", "success");
    });

    this.load.image(
      SYMBOL_SEQUENCE_FONT_IMAGE_KEY,
      SYMBOL_SEQUENCE_FONT_IMAGE_PATH,
    );
    const fusion10Font = SYMBOL_SEQUENCE_FONT_CONFIGS["10px"];
    this.load.image(fusion10Font.imageKey, fusion10Font.imagePath);
    this.load.image(NOTE_OPTIONS_FONT_IMAGE_KEY, NOTE_OPTIONS_FONT_IMAGE_PATH);
    for (const config of SIMSUN_ASCII_FONT_CONFIG_LIST) {
      this.load.image(config.imageKey, config.imagePath);
    }
    this.load.image(
      SYMBOL_SPRITE_FONT_IMAGE_KEY,
      SYMBOL_SPRITE_FONT_IMAGE_PATH,
    );
    this.load.image(
      LEVEL0_UNPATTERNED_IMAGE_KEY,
      LEVEL0_UNPATTERNED_IMAGE_PATH,
    );
    this.load.audio(HOME_BGM_KEY, HOME_BGM_PATH);
    this.load.audio(LEVEL_BGM_KEY, LEVEL_BGM_PATH);
    this.load.audio(LEVEL0_BGM_KEY, LEVEL0_BGM_PATH);
    this.load.audio(LEVEL_SELECT_BGM_KEY, LEVEL_SELECT_BGM_PATH);
  }

  public create(): void {
    try {
      if (this.loaderFailureMessage !== null) {
        throw new Error(`Boot preload failed at ${this.loaderFailureMessage}`);
      }

      pushBootLoadingLog("BOOT", "registering bitmap fonts");
      setBootLoadingProgress(94, "registering bitmap fonts");
      const runtimeSession = getRuntimeSession();
      const config = createBootConfiguration(runtimeSession.getCurrentSave());

      this.sys.cache.bitmapFont.add(
        SYMBOL_SEQUENCE_FONT_KEY,
        Phaser.GameObjects.RetroFont.Parse(this, {
          image: SYMBOL_SEQUENCE_FONT_IMAGE_KEY,
          width: SYMBOL_SEQUENCE_FONT_CELL_WIDTH,
          height: SYMBOL_SEQUENCE_FONT_CELL_HEIGHT,
          chars: SYMBOL_SEQUENCE_FONT_CHARS,
          charsPerRow: SYMBOL_SEQUENCE_FONT_CHARS_PER_ROW,
          "offset.x": 0,
          "offset.y": 0,
          "spacing.x": 0,
          "spacing.y": 0,
          lineSpacing: 0,
        }),
      );

      this.sys.cache.bitmapFont.add(
        NOTE_OPTIONS_FONT_KEY,
        Phaser.GameObjects.RetroFont.Parse(this, {
          image: NOTE_OPTIONS_FONT_IMAGE_KEY,
          width: NOTE_OPTIONS_FONT_CELL_WIDTH,
          height: NOTE_OPTIONS_FONT_CELL_HEIGHT,
          chars: SYMBOL_SEQUENCE_FONT_CHARS,
          charsPerRow: NOTE_OPTIONS_FONT_CHARS_PER_ROW,
          "offset.x": 0,
          "offset.y": 0,
          "spacing.x": 0,
          "spacing.y": 0,
          lineSpacing: 0,
        }),
      );

      const fusion10Font = SYMBOL_SEQUENCE_FONT_CONFIGS["10px"];
      this.registerBitmapFont(fusion10Font);

      for (const config of SIMSUN_ASCII_FONT_CONFIG_LIST) {
        this.registerBitmapFont(config);
      }

      this.sys.cache.bitmapFont.add(
        SYMBOL_SPRITE_FONT_KEY,
        Phaser.GameObjects.RetroFont.Parse(this, {
          image: SYMBOL_SPRITE_FONT_IMAGE_KEY,
          width: SYMBOL_SPRITE_FONT_CELL_WIDTH,
          height: SYMBOL_SPRITE_FONT_CELL_HEIGHT,
          chars: SYMBOL_SPRITE_FONT_CHARS,
          charsPerRow: SYMBOL_SPRITE_FONT_CHARS_PER_ROW,
          "offset.x": 0,
          "offset.y": 0,
          "spacing.x": 0,
          "spacing.y": 0,
          lineSpacing: 0,
        }),
      );

      pushBootLoadingLog("OK", "boot scene ready", "success");
      setBootLoadingProgress(100, "boot scene ready");
      markBootLoadingReady();
      this.scene.start("HomeScene", {
        bootConfiguration: config,
      } satisfies HomeSceneInitData);
    } catch (error) {
      markBootLoadingFailed(
        "Failed to prepare boot resources",
        error instanceof Error ? error.message : String(error),
      );
      const debugRoot = document.querySelector<HTMLElement>("#debug-root");
      if (debugRoot !== null) {
        new DebugOverlay().renderError(
          debugRoot,
          error instanceof Error ? error.message : String(error),
        );
      }
    }
  }

  private registerBitmapFont(config: BitmapFontRegistrationConfig): void {
    this.sys.cache.bitmapFont.add(
      config.bitmapFontKey,
      Phaser.GameObjects.RetroFont.Parse(this, {
        image: config.imageKey,
        width: config.cellWidth,
        height: config.cellHeight,
        chars: config.chars,
        charsPerRow: config.charsPerRow,
        "offset.x": 0,
        "offset.y": 0,
        "spacing.x": 0,
        "spacing.y": 0,
        lineSpacing: 0,
      }),
    );
  }
}
